/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Tiny helpers that let any code path (agent tools, context menu, selection
 * HUD, the transform gizmo) signal the Molecular Modeling simulation worker
 * without importing the React control component. They bump primitive-store
 * flags that simulationControls watches:
 *   - markEngineDirty({ moved }): the mirror engine's atoms were edited → post
 *     syncState; `moved` (atoms were displaced) also re-pins the Reset target
 *   - bumpEngineRebuild(): a fresh mirror engine was built → re-init the worker
 *   - requestTemperatureChange(pct): a heat/cool that must reach the worker
 *   - requestPistonPlate(position, velocity): a plate placement that must reach
 *     the worker (setPistonPlateHeight below)
 *
 * On the main-thread fallback (no worker) the mirror IS the stepped engine, so
 * there is nothing to ship — but markEngineDirty() still matters there: the
 * controls' sync does the engine-side housekeeping an out-of-integrator edit
 * needs (Verlet-list invalidation, forces re-evaluated at the edited geometry,
 * the Reset target re-pinned) on whichever engine steps.
 */
import { usePrimitiveStore } from '../stores/commonPrimitive.ts';
import { useStore } from '../stores/common.ts';
import { useRefStore } from '../stores/commonRef.ts';
import type { Vector3 } from 'three';
import i18n from '../i18n/i18n';
import { setMessage } from '../helpers.tsx';

// Manually place the piston plate at height `heightAboveFloorAng` (Å, 0 = box
// floor, lz = ceiling). Keeps the three representations of the plate consistent:
// the live mirror engine (so the rendered plate jumps immediately), the persisted
// `pistonSnapshot` (so it survives save/reload and seeds a paused reopen), and the
// off-thread worker (a `setPistonPlate` message, applied between chunks like a
// heat/cool). It used to reach the worker by REBUILDING the engine, and a rebuilt
// engine starts at step 0 — the controls read that as a Reset and wiped every
// series and tape, so nudging the plate (or switching the piston on) mid-run
// blanked the graphs and the IR / MSD recordings while the gas stayed compressed.
// Clamped to the plate's travel range. Used by the Piston Position control, the
// piston switch (plate parked at the ceiling on enable), the agent's
// configureSimulation, and to restore a sane plate after a constant-volume
// (piston-off) sweep, where the gas has expanded to fill the whole box.
export const setPistonPlateHeight = (heightAboveFloorAng: number): void => {
  const ps = useStore.getState().projectState;
  const lz = ps.molecularContainer.lz;
  const hz = lz / 2;
  // Plate Z is origin-centered; movePiston bounds it to [-hz + 2, hz].
  const position = Math.min(hz, Math.max(-hz + 2, heightAboveFloorAng - hz));
  const md = useRefStore.getState().molecularDynamicsRef?.current;
  if (md) {
    // `pistonEnabled` too: the switch's enable path sets the store flag a moment
    // before calling here, and the rendered plate (modelContainer) reads the
    // mirror's flag — without a rebuild nothing else copies it over while paused.
    md.pistonEnabled = ps.pistonEnabled ?? false;
    md.pistonPosition = position;
    md.pistonVelocity = 0;
    md.pistonInitialized = true;
  }
  useStore.getState().set((s) => {
    s.projectState.pistonSnapshot = { position, velocity: 0 };
  });
  usePrimitiveStore.getState().setChanged(true);
  requestPistonPlate(position, 0);
};

// The barostat moves the periodic cell in the engine, not in the store: the
// store keeps the box the user SET, the mirror the live edges the worker ships.
// Before anything turns the live cell back into a wall — periodic switched
// off, a reactive run — the store box must become the live cell, or the walls
// land inside the gas. Also what a save writes. No-op when nothing moved.
export const adoptLiveCell = (): void => {
  const md = useRefStore.getState().molecularDynamicsRef?.current;
  if (!md) return;
  const c = md.container;
  const ps = useStore.getState().projectState;
  const b = ps.molecularContainer;
  if (!(b.periodic ?? false)) return;
  if (c.lx === b.lx && c.ly === b.ly && c.lz === b.lz) return;
  useStore.getState().set((s) => {
    s.projectState.molecularContainer.lx = c.lx;
    s.projectState.molecularContainer.ly = c.ly;
    s.projectState.molecularContainer.lz = c.lz;
  });
  md.pinContainer();
};

// A positional restraint tethers an atom to an ANCHOR (Restraint.position, the
// atom's position when the restraint was set). Any edit that moves the atom
// outside the integrator (gizmo drag, context-menu Translate / Rotate, the
// agent's transformLabMolecule) must move the anchor by the same transform,
// or Play pulls the molecule back toward the old spot with a k·Δr spring
// (10 Å at 10 eV/Å² is a 100 eV/Å force — the run snaps back or blows up).
// `transform` mutates the given vector in place. No-op without a restraint.
export const transformRestraintAnchor = (
  atom: { restraint?: { position: Vector3 } },
  transform: (anchor: Vector3) => void,
): void => {
  if (atom.restraint) transform(atom.restraint.position);
};

// Store-side twin: copy the LIVE atom's (already transformed) anchor onto the
// persisted testMolecule atom, so the saved project carries the moved anchor
// too. Call inside a store `set` (the store atom is an Immer draft).
export const copyRestraintAnchorToStore = (
  storeAtom: { restraint?: { position: { x: number; y: number; z: number } } },
  liveAtom: { restraint?: { position: Vector3 } },
): void => {
  if (!storeAtom.restraint || !liveAtom.restraint) return;
  storeAtom.restraint.position.x = liveAtom.restraint.position.x;
  storeAtom.restraint.position.y = liveAtom.restraint.position.y;
  storeAtom.restraint.position.z = liveAtom.restraint.position.z;
};

// Reset restores only positions + velocities (Atom.reset). An edit that MOVES
// atoms (gizmo, Translate / Rotate, transformLabMolecule, fitBoxToLab) replaces
// that start — the hand-built arrangement is what the user means to start from
// — so it pins. A parameter edit (charge, damping, restraint, fix) changes
// nothing Reset restores and survives Reset by itself; pinning on it used to
// overwrite the t = 0 start with whatever mid-run state was paused. Latched
// until the next paused sync consumes it, so a move is never lost to a
// parameter edit (or a paramsOnly sync) that bumps the flag in between.
let pendingMovePin = false;

export const takePendingMovePin = (): boolean => {
  const pin = pendingMovePin;
  pendingMovePin = false;
  return pin;
};

// A loaded lab state (keepLabState / "Show final state") sets the mirror's
// clock (indexOfStep + time) to the kept frame's. The worker integrates its own
// copy, so the next paused sync must ship the clock too — otherwise the worker
// stayed at the step it had, its first packet put that back on the mirror, and
// the HUD read 0 fs for a frame the card called t = 300 ps. Latched like the
// move pin, and consumed (shipped or discarded) by the next sync.
let pendingClockShip = false;

export const takePendingClockShip = (): boolean => {
  const ship = pendingClockShip;
  pendingClockShip = false;
  return ship;
};

// Undoable.blocked for entries that MOVE atoms: refuse while the simulation
// runs (the worker owns positions; a mirror-side restore is lost to the next
// packet while the undo stack still pops — the same reason the move tools are
// disabled mid-run).
// Also while a PAUSE is settling (the flag is off but the worker's final frame has
// not landed): a move made then is shipped parameters-only, then overwritten by
// that frame — and its latched Reset-target pin fired on the next Play, pinning
// the paused mid-run frame and clearing the IR / velocity / position tapes for an
// edit the user never saw take effect. Typically tens of ms; 100s on big labs.
// Paced runs: a run someone is WATCHING (Play — the button, VR gaze, the
// agent's controlSimulation run) steps at most one chunk (refreshInterval
// steps) per display frame, the pace the lab had before the worker went
// free-running on 2026-10-07. Free-run made cheap labs (a few hundred argon
// atoms) run many times faster than anyone can follow, and a smaller timestep
// did not help enough. Runs that MEASURE — runMolecularDynamics, the spectrum
// tools, the sweeps — set startSimulation without marking, so they keep the
// full compute rate. Cleared whenever the run stops.
// While an agent tool runs (holdFullSpeed, taken by the agent runner around
// every handler) the run is NOT paced even if Play marked it: a measuring tool
// called on a run the user had started kept the watched pace, so its
// picoseconds took as long as pressing Play. The pace returns when the tool
// ends, if the run is still going. A tool whose run is for watching — a
// wall-clock "run for a minute" — opts back in with whileWatched.
let pacedRun = false;
let fullSpeedHolds = 0;
let watchedDepth = 0;
export const markPacedRun = (): void => {
  pacedRun = true;
};
export const clearPacedRun = (): void => {
  pacedRun = false;
};
export const isPacedRun = (): boolean => pacedRun && (fullSpeedHolds === 0 || watchedDepth > 0);
export const holdFullSpeed = (): (() => void) => {
  fullSpeedHolds++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    fullSpeedHolds--;
  };
};
export const whileWatched = async <T>(fn: () => Promise<T>): Promise<T> => {
  watchedDepth++;
  try {
    return await fn();
  } finally {
    watchedDepth--;
  }
};

// Step target for a run that must stop at an exact step count (the agent's
// runMolecularDynamics with fs / ps / steps). The free-running worker is far
// ahead of the snapshots the main thread sees, so stopping from here — poll the
// mirror, then send Pause — overshot by thousands of steps on a cheap lab (5 ps
// asked, 7.5–9.6 ps run). The worker holds at this step instead, and the wait
// then pauses a lab already standing there. An ABSOLUTE step index of the
// current engine; null = no target. Rides every run / ack.
let runStopAtStep: number | null = null;
export const setRunStopAtStep = (step: number | null): void => {
  runStopAtStep = step;
};
export const getRunStopAtStep = (): number | null => runStopAtStep;

export const blockedWhileSimulating = (): boolean => {
  const p = usePrimitiveStore.getState();
  return !!p.startSimulation || !!p.mdPauseSettling;
};

// A FIRE relax (Minimize Energy) owns the lab's atoms until it finishes and
// writes its relaxed frame back by atom index, so a molecule removed or added
// meanwhile would land the remaining molecules on the wrong coordinates. The
// user-facing delete / cut paths refuse while it runs, with a toast (the relax
// also discards its result if the list changed anyway — an agent tool, the
// gallery). Heat / Cool refuse too (`messageKey` 'WaitForMinimizeHeat'): the
// relax brings the atoms to rest and ships ITS velocities to the worker when it
// ends, so a rescale meanwhile is undone on the spot — and the worker's reply
// used to snap the relaxing atoms back to the pre-relax frame (the controls now
// drop atom data from packets that land during a relax). Returns true when the
// caller should bail out.
export const refuseWhileMinimizing = (messageKey = 'WaitForMinimizeEdit'): boolean => {
  if (!usePrimitiveStore.getState().minimizing) return false;
  setMessage('warning', i18n.t(`experiment.${messageKey}`, { lng: useStore.getState().language }), 8);
  return true;
};

export const markEngineDirty = (opts: { moved?: boolean; clock?: boolean } = {}): void => {
  if (opts.moved) pendingMovePin = true;
  if (opts.clock) pendingClockShip = true;
  usePrimitiveStore.getState().set((s) => {
    s.syncEngineFlag = (s.syncEngineFlag ?? 0) + 1;
  });
};

export const bumpEngineRebuild = (): void => {
  usePrimitiveStore.getState().set((s) => {
    s.engineRebuildFlag = (s.engineRebuildFlag ?? 0) + 1;
  });
};

export const requestTemperatureChange = (percent: number): void => {
  usePrimitiveStore.getState().set((s) => {
    s.pendingTempChangePercent = percent;
  });
};

// Plate Z (origin-centered, Å) + velocity (Å/fs) for the worker engine; the
// controls post it as `setPistonPlate`. Posted messages are handled in order, so
// a placement followed at once by Play lands before that Play's `run`.
export const requestPistonPlate = (position: number, velocity: number): void => {
  usePrimitiveStore.getState().set((s) => {
    s.pendingPistonPlate = { position, velocity };
  });
};

// "Paused" has two moments: startSimulation flips false at once, and a chunk
// later the worker's `paused` packet levels the mirror with it (the controls
// clear `mdPauseSettling` then). In between, the mirror is stale and about to be
// overwritten — an edit made then is lost (a spark vanishes, a fitBoxToLab
// recenter is undone while the box still shrinks). A human can't act inside that
// window; the agent's `controlSimulation pause` + spark / minimizeLab /
// fitBoxToLab in one turn lands squarely in it. Resolves true once the lab is
// paused AND settled, false if that hasn't happened within `timeoutMs` (or Play
// was pressed again meanwhile). Yields one macrotask first: the controls post
// `pause` from the Play effect's cleanup, which may not have run yet when the
// flag was flipped a moment ago.
export const awaitPauseSettled = (timeoutMs = 1500): Promise<boolean> =>
  new Promise((resolve) => {
    const settled = () => {
      const s = usePrimitiveStore.getState();
      return !s.startSimulation && !s.mdPauseSettling;
    };
    setTimeout(() => {
      if (settled()) return resolve(true);
      const unsub = usePrimitiveStore.subscribe(() => {
        if (!settled()) return;
        clearTimeout(timer);
        unsub();
        resolve(true);
      });
      const timer = setTimeout(() => {
        unsub();
        resolve(settled());
      }, timeoutMs);
    }, 0);
  });

// Reset and heat/cool are asynchronous too: the tool raises a flag / files a
// request, the controls' effect posts to the worker, and only the `reset` /
// `tempChanged` reply moves the mirror (t = 0 and the plate at the ceiling; the
// rescaled velocities). The controls bump `mdControlReplySeq` when that reply has
// been applied (or the no-worker fallback has run). Read the counter BEFORE
// raising the request, then await a bump past it; resolves true once landed,
// false if nothing arrived within `timeoutMs` (worker holds no engine, lab being
// rebuilt…) — the caller should then label its snapshot as unsettled.
export const controlReplySeq = (): number => usePrimitiveStore.getState().mdControlReplySeq;

export const awaitControlReply = (seqBefore: number, timeoutMs = 1500): Promise<boolean> =>
  new Promise((resolve) => {
    const landed = () => usePrimitiveStore.getState().mdControlReplySeq > seqBefore;
    if (landed()) return resolve(true);
    const unsub = usePrimitiveStore.subscribe(() => {
      if (!landed()) return;
      clearTimeout(timer);
      unsub();
      resolve(true);
    });
    const timer = setTimeout(() => {
      unsub();
      resolve(landed());
    }, timeoutMs);
  });

export const bumpControlReply = (): void => {
  usePrimitiveStore.getState().set((s) => {
    s.mdControlReplySeq = (s.mdControlReplySeq ?? 0) + 1;
  });
};

// One atom's position, addressed by molecule + atom index so it can be
// written back into both the live Molecule (rendered) and the persisted
// projectState.testMolecules copy (saved / reloaded).
export interface LabAtomPosition {
  molIdx: number;
  atomIdx: number;
  x: number;
  y: number;
  z: number;
  // The restraint anchor that goes with this position (present only for a
  // restrained atom). A drag must carry the anchor along, or Play pulls the
  // molecule back to where it was tethered — see engineSync.transformRestraintAnchor.
  anchor?: { x: number; y: number; z: number };
}

// Write a set of lab atom positions into the live molecules and the saved project,
// tell the worker, and repaint — the one commit path for moving molecules by hand
// (the transform gizmo's drag end, the X/Y/Z keyboard nudge, and their undo/redo).
export const writeLabAtomPositions = (list: LabAtomPosition[]): void => {
  const molecules = useRefStore.getState().moleculesRef?.current;
  if (molecules) {
    for (const p of list) {
      const a = molecules[p.molIdx]?.atoms[p.atomIdx];
      if (a) {
        a.position.set(p.x, p.y, p.z);
        // Inform the MD engine: its atoms ARE these runtime atoms, and
        // MolecularDynamics.reset() snaps each position back to its
        // initialPosition. Without syncing initialPosition, starting
        // (or resetting) the simulation would discard the move
        // and the molecule would jump back to where it was.
        a.initialPosition?.copy(a.position);
        if (p.anchor && a.restraint) a.restraint.position.set(p.anchor.x, p.anchor.y, p.anchor.z);
      }
    }
  }
  useStore.getState().set((state) => {
    for (const p of list) {
      const a = state.projectState.testMolecules[p.molIdx]?.atoms[p.atomIdx];
      if (a) {
        a.position.x = p.x;
        a.position.y = p.y;
        a.position.z = p.z;
        const live = molecules?.[p.molIdx]?.atoms[p.atomIdx];
        if (p.anchor && live) copyRestraintAnchorToStore(a, live);
      }
    }
  });
  usePrimitiveStore.getState().updateViewer();
  usePrimitiveStore.getState().setChanged(true);
  // The worker engine holds its own copy of the atoms: tell the controls to
  // post a syncState (with the Reset target pinned to the new positions, as
  // above). Resuming alone adopts positions but deliberately does NOT re-pin.
  markEngineDirty({ moved: true });
};
